import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_ORDER_SERVICE } from '@/api/constant';
import Breadcrumb from '@/components/common/Breadcrumb';
import CommonTable, {
  TableColumn,
} from '@/components/common/commonTable/CommonTable';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import DetailsCard from '@/components/UI/DetailsCard';
import FormField from '@/components/UI/FormField';
import { usePermissions } from '@/hooks/usePermissions';
import { ActionConfig } from '@/interfaces/common.interface';
import {
  ICouponResponseData,
  ICouponUsages,
  RecentUsage,
} from '@/interfaces/cuponResponse.interface';
import { formatDateTime, toFixedThree } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const ViewCoupon = () => {
  const navigate = useNavigate();
  const { canView } = usePermissions();
  const { code } = useParams();

  const [coupon, setCoupon] = useState<ICouponResponseData | null>(null);
  const [couponsUsages, setCouponUsages] = useState<ICouponUsages | null>(null);

  const fetchCoupon = useCallback(async () => {
    if (!code) return;

    try {
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.coupons}/${code}`;
      const res = await callApi<ICouponResponseData>(PATH, { method: 'GET' });
      setCoupon(res);
    } catch (error) {
      ThrowError(error);
    }
  }, [code]);

  const fetchRecentUsages = useCallback(async () => {
    if (!coupon?.id) return;

    try {
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.coupons}/${coupon.id}/usage-stats`;
      const res = await callApi<ICouponUsages>(PATH, { method: 'GET' });
      setCouponUsages(res);
    } catch (error) {
      ThrowError(error);
    }
  }, [coupon?.id]);

  useEffect(() => {
    fetchCoupon();
  }, [fetchCoupon]);

  useEffect(() => {
    fetchRecentUsages();
  }, [fetchRecentUsages]);

  const productActionConfig: Record<string, ActionConfig<RecentUsage>> = {
    viewUser: {
      text: 'View User',
      onClick: (i) => navigate(`${RoutesLink.customerDetails}/${i.userId}`),
      permission: () => canView('user'),
    },
    viewOrder: {
      text: 'View Order',
      onClick: (i) => {
        navigate(`${RoutesLink.orderDetail}/${i.orderId}`);
      },
      permission: () => canView('order'),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(productActionConfig)
        .filter((action) => action.permission())
        .map(({ permission, ...rest }) => rest),
    [canView],
  );

  if (!coupon || !couponsUsages) return null;

  const columns: TableColumn<RecentUsage>[] = [
    {
      key: 'orderId',
      header: 'Order Id',
      render: (i) => (
        <span
          className={`capitalize ${i?.order?.personalInfo?.isEmployee ? 'text-blue-500' : ''}`}
        >
          {i.order.orderId}
        </span>
      ),
    },
    {
      key: 'firstName',
      header: 'firstName',
      render: (i) => (
        <span
          className={`capitalize ${i?.order?.personalInfo?.isEmployee ? 'text-blue-500' : ''}`}
        >
          {i?.order?.personalInfo?.firstName || '--'}
        </span>
      ),
    },
    {
      key: 'lastName',
      header: 'lastName',
      render: (i) => (
        <span
          className={`capitalize ${i?.order?.personalInfo?.isEmployee ? 'text-blue-500' : ''}`}
        >
          {i?.order?.personalInfo?.lastName || '--'}
        </span>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (i) => (
        <span
          className={`capitalize ${i?.order?.personalInfo?.isEmployee ? 'text-blue-500' : ''}`}
        >
          {i?.order?.personalInfo?.email || '--'}
        </span>
      ),
    },
  ];

  const breadcrumb = [
    { label: 'Coupons', href: RoutesLink.coupons },
    { label: 'View Coupon' },
  ];

  const usageLimits = [
    ['maxTotalUsage', 'Max Total Usage'],
    ['maxUsagePerUser', 'Max Usage Per User'],
    ['maxUsagePerDay', 'Max Usage Per Day'],
    ['maxUsagePerUserPerDay', 'Max Usage Per User Per Day'],
  ] as const;

  const couponUseInfo = [
    { label: 'Total Usage', value: couponsUsages.totalUsage },
    {
      label: 'Total Discount',
      value: toFixedThree(couponsUsages.totalDiscountGiven),
    },
    { label: 'Unique Users', value: couponsUsages.uniqueUsers },
    { label: 'Today Usages', value: couponsUsages.usageToday },
  ];

  return (
    <Layout>
      <Breadcrumb items={breadcrumb} />

      <div className="space-y-5">
        {/* BASIC INFO */}
        <Card>
          <div className="grid md:grid-cols-2 gap-3">
            <FormField label="Title" value={coupon.name} disabled />
            <FormField label="Coupon Code" value={coupon.code} disabled />
          </div>
          <div className="mt-3">
            <FormField
              label="Description"
              type="textarea"
              rows={4}
              value={coupon.description}
              disabled
            />
          </div>
        </Card>

        {/* DISCOUNT */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <h2 className="text-lg font-semibold mb-3">Discount Value</h2>

            <div className="grid md:grid-cols-2 gap-3">
              <FormField label="Discount Type" value={coupon.type} disabled />

              <FormField
                label="Discount Value"
                value={
                  coupon.type === 'percentage'
                    ? `${coupon.discountValue}%`
                    : `BD ${coupon.discountValue}`
                }
                disabled
              />
            </div>

            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <FormField
                label="Max Discount"
                value={coupon.maxDiscount}
                disabled
              />
              <FormField
                label="Min Purchase Value"
                value={`BD ${coupon.minimumValue}`}
                disabled
              />
            </div>
          </Card>
          <DetailsCard title="Coupon Usages Info" details={couponUseInfo} />
        </div>

        {/* USAGE LIMITS */}
        <Card>
          <h2 className="text-lg font-semibold mb-4">Usage Limits</h2>

          <div className="grid md:grid-cols-2 gap-4">
            {usageLimits.map(([key, label]) => {
              const value = coupon[key];

              return (
                <FormField
                  key={key}
                  label={label}
                  value={value === null ? 'Unlimited' : value}
                  disabled
                />
              );
            })}
          </div>
        </Card>
        {/* ACTIVE DATES */}
        <Card>
          <h2 className="text-lg font-semibold mb-4">Active Dates</h2>

          <div className="grid md:grid-cols-2 gap-3">
            <FormField
              label="Start Date & Time"
              value={formatDateTime(coupon.startDate, 'YYMMMDDHH')}
              disabled
            />
            <FormField
              label="End Date & Time"
              value={formatDateTime(coupon.expirationDate, 'YYMMMDDHH')}
              disabled
            />
          </div>
        </Card>
        <Card>
          <h2 className="text-lg font-semibold mb-4">Coupon Usages</h2>
          <CommonTable
            data={couponsUsages.recentUsages}
            columns={columns}
            showCheckbox={false}
            actions={actions}
          />
        </Card>
      </div>
    </Layout>
  );
};

export default ViewCoupon;
